import { injectable } from "inversify";
import { makeAutoObservable, runInAction } from "mobx";
import type {
  DeviceCheckPhase,
  IDemoDeviceCheckManager,
} from "@/lib/types/demo-session";

const SILENCE_MS = 5000;
const THRESHOLD_DBFS = -45;
const THRESHOLD_HOLD_MS = 150;
const METER_MIN_DBFS = -60;
/** dBFS treated as "full" on the 12-bar meter — normal speech peaks well
 * under 0 dBFS, so clamping the top here keeps the bars from looking dead. */
const METER_MAX_DBFS = -12;
const METER_BAR_COUNT = 12;
const MUTE_DEBOUNCE_MS = 1500;

function getErrorName(error: unknown): string {
  return error instanceof DOMException ? error.name : "";
}

/** Its stream is handed to the session via claimStreams() and stopped after the SDK is pinned to the same device. */
@injectable()
export class DemoDeviceCheckStore implements IDemoDeviceCheckManager {
  phase: DeviceCheckPhase = "requesting";
  micMuted: boolean = false;
  /** Latched once the mic reaches "detected"; muting afterwards must not revoke the pass. */
  micVerified: boolean = false;
  litBars: number = 0;
  cameraEnabled: boolean = false;
  cameraStream: MediaStream | null = null;
  cameraDeniedMessage: string | null = null;
  micStream: MediaStream | null = null;
  audioDevices: MediaDeviceInfo[] = [];
  speakerDevices: MediaDeviceInfo[] = [];
  cameraDevices: MediaDeviceInfo[] = [];
  selectedMicId: string = "default";
  selectedSpeakerId: string = "default";
  selectedCameraId: string = "default";
  switchingLabel: string | null = null;

  private audioContext: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private sourceNode: MediaStreamAudioSourceNode | null = null;
  private rafId: number | null = null;
  private silenceTimer: ReturnType<typeof setTimeout> | null = null;
  private aboveThresholdMs = 0;
  private lastFrameTime = 0;
  private micPermissionStatus: PermissionStatus | null = null;
  private listenersRegistered = false;
  private micTrack: MediaStreamTrack | null = null;
  private muteDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  private cameraGeneration = 0;
  /** Bumped per attempt so a stale getUserMedia result is dropped. */
  private generation = 0;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  get micHeard(): boolean {
    return this.phase === "detected" && !this.micMuted;
  }

  async start(): Promise<void> {
    const gen = ++this.generation;
    this.teardownMicStream();
    this.clearPermissionWatch();
    runInAction(() => {
      this.phase = "requesting";
      this.micVerified = false;
    });

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio:
          this.selectedMicId && this.selectedMicId !== "default"
            ? { deviceId: { exact: this.selectedMicId } }
            : true,
      });
      if (gen !== this.generation) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      this.attachMicStream(stream);
      await this.refreshDevices();
      this.registerRecoveryListeners();
    } catch (error) {
      if (gen !== this.generation) return;
      this.handleGetUserMediaError(error);
    }
  }

  async selectMic(deviceId: string): Promise<void> {
    const gen = ++this.generation;
    const label =
      this.audioDevices.find((d) => d.deviceId === deviceId)?.label ?? null;

    runInAction(() => {
      this.phase = "switching";
      this.micVerified = false;
      this.switchingLabel = label;
      this.selectedMicId = deviceId;
    });
    this.teardownMicStream();

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { deviceId: { exact: deviceId } },
      });
      if (gen !== this.generation) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      this.attachMicStream(stream);
    } catch (error) {
      if (gen !== this.generation) return;
      this.handleGetUserMediaError(error);
    }
  }

  async toggleCamera(enabled: boolean): Promise<void> {
    const gen = ++this.cameraGeneration;

    if (!enabled) {
      this.cameraStream?.getTracks().forEach((track) => track.stop());
      runInAction(() => {
        this.cameraStream = null;
        this.cameraEnabled = false;
        this.cameraDeniedMessage = null;
      });
      return;
    }

    // Stop the old preview first or it leaks.
    this.cameraStream?.getTracks().forEach((track) => track.stop());
    runInAction(() => {
      this.cameraStream = null;
    });

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          facingMode: "user",
          ...(this.selectedCameraId && this.selectedCameraId !== "default"
            ? { deviceId: { exact: this.selectedCameraId } }
            : {}),
        },
      });
      if (gen !== this.cameraGeneration) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      runInAction(() => {
        this.cameraStream = stream;
        this.cameraEnabled = true;
        this.cameraDeniedMessage = null;
      });

      const settledDeviceId = stream.getVideoTracks()[0]?.getSettings().deviceId;
      if (settledDeviceId) {
        runInAction(() => {
          this.selectedCameraId = settledDeviceId;
        });
      }
      // Labels are blank until camera permission is granted — refresh now that it is.
      void this.refreshDevices().catch(() => {});
    } catch (error) {
      if (gen !== this.cameraGeneration) return;
      const name = getErrorName(error);
      runInAction(() => {
        this.cameraEnabled = false;
        this.cameraStream = null;
        this.cameraDeniedMessage =
          "Camera blocked — you can still start without it.";
        if (name === "NotFoundError" || name === "OverconstrainedError") {
          this.selectedCameraId = "default";
        }
      });
    }
  }

  async selectCamera(deviceId: string): Promise<void> {
    runInAction(() => {
      this.selectedCameraId = deviceId;
    });
    if (this.cameraEnabled) {
      await this.toggleCamera(true);
    }
  }

  selectSpeaker(deviceId: string): void {
    runInAction(() => {
      this.selectedSpeakerId = deviceId;
    });
  }

  toggleMic(enabled: boolean): void {
    runInAction(() => {
      this.micMuted = !enabled;
    });
    if (this.micTrack) {
      this.micTrack.enabled = enabled;
    }
  }

  claimStreams(): {
    micStream: MediaStream | null;
    cameraStream: MediaStream | null;
  } {
    const { micStream, cameraStream } = this;

    if (micStream) {
      this.stopMeter();
      this.clearSilenceTimer();
      // Detach first, or our own handoff would misfire the mic-lost watch.
      this.detachMicTrackListeners();
    }

    this.micStream = null;
    this.cameraStream = null;
    return { micStream, cameraStream };
  }

  adoptCameraStream(stream: MediaStream): void {
    this.cameraGeneration++;
    this.cameraStream?.getTracks().forEach((track) => track.stop());
    runInAction(() => {
      this.cameraStream = stream;
      this.cameraEnabled = true;
    });
  }

  dispose(): void {
    this.generation++;
    this.cameraGeneration++;
    this.clearSilenceTimer();
    this.stopMeter();
    this.clearPermissionWatch();
    this.detachMicTrackListeners();

    this.micStream?.getTracks().forEach((track) => track.stop());
    this.cameraStream?.getTracks().forEach((track) => track.stop());

    if (typeof navigator !== "undefined" && this.listenersRegistered) {
      navigator.mediaDevices.removeEventListener(
        "devicechange",
        this.handleDeviceChange,
      );
    }

    // DI singleton reused across demo runs — reset everything.
    runInAction(() => {
      this.phase = "requesting";
      this.micMuted = false;
      this.micVerified = false;
      this.litBars = 0;
      this.cameraEnabled = false;
      this.cameraStream = null;
      this.cameraDeniedMessage = null;
      this.micStream = null;
      this.audioDevices = [];
      this.speakerDevices = [];
      this.cameraDevices = [];
      this.selectedMicId = "default";
      this.selectedSpeakerId = "default";
      this.selectedCameraId = "default";
      this.switchingLabel = null;
      this.listenersRegistered = false;
    });
  }

  private attachMicStream(stream: MediaStream): void {
    runInAction(() => {
      this.micStream = stream;
      this.phase = "listening";
      this.switchingLabel = null;
    });

    const track = stream.getAudioTracks()[0];
    const settledDeviceId = track?.getSettings().deviceId;
    if (settledDeviceId) {
      runInAction(() => {
        this.selectedMicId = settledDeviceId;
      });
    }

    this.micTrack = track ?? null;
    if (track) track.enabled = !this.micMuted;
    // toggleMic only flips `track.enabled`, which never fires mute, so our own click can't trigger these.
    track?.addEventListener("ended", this.handleMicLost);
    track?.addEventListener("mute", this.handleMicMuted);
    track?.addEventListener("unmute", this.handleMicUnmuted);

    this.aboveThresholdMs = 0;
    this.startMeter(stream);
    this.armSilenceTimer();
  }

  private handleMicLost = (): void => {
    this.clearMuteDebounce();
    this.stopMeter();
    this.clearSilenceTimer();
    // Stop and detach before dropping the ref, or the capture stays open and stale listeners fire later.
    this.detachMicTrackListeners();
    this.micStream?.getTracks().forEach((track) => track.stop());
    runInAction(() => {
      this.micStream = null;
      this.phase = "no-device";
      this.micVerified = false;
      // Device is gone — restart on whatever mic remains.
      this.selectedMicId = "default";
    });
    void this.refreshDevices().catch(() => {});
  };

  // Chrome/macOS fire transient mute→unmute pairs, so a bare "mute" isn't
  // treated as loss until it holds for MUTE_DEBOUNCE_MS with no "unmute".
  private handleMicMuted = (): void => {
    this.clearMuteDebounce();
    this.muteDebounceTimer = setTimeout(() => {
      this.muteDebounceTimer = null;
      this.handleMicLost();
    }, MUTE_DEBOUNCE_MS);
  };

  private handleMicUnmuted = (): void => {
    this.clearMuteDebounce();
  };

  private clearMuteDebounce(): void {
    if (this.muteDebounceTimer) {
      clearTimeout(this.muteDebounceTimer);
      this.muteDebounceTimer = null;
    }
  }

  private detachMicTrackListeners(): void {
    this.clearMuteDebounce();
    this.micTrack?.removeEventListener("ended", this.handleMicLost);
    this.micTrack?.removeEventListener("mute", this.handleMicMuted);
    this.micTrack?.removeEventListener("unmute", this.handleMicUnmuted);
    this.micTrack = null;
  }

  private armSilenceTimer(): void {
    this.clearSilenceTimer();
    this.silenceTimer = setTimeout(() => {
      runInAction(() => {
        if (this.phase === "listening") {
          this.phase = "silent";
        }
      });
    }, SILENCE_MS);
  }

  private clearSilenceTimer(): void {
    if (this.silenceTimer) {
      clearTimeout(this.silenceTimer);
      this.silenceTimer = null;
    }
  }

  private startMeter(stream: MediaStream): void {
    this.stopMeter();

    try {
      const audioContext = new AudioContext();
      this.audioContext = audioContext;
      void audioContext.resume();

      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 2048;
      analyser.smoothingTimeConstant = 0;

      const sourceNode = audioContext.createMediaStreamSource(stream);
      sourceNode.connect(analyser);

      this.analyser = analyser;
      this.sourceNode = sourceNode;
      this.lastFrameTime = performance.now();

      const data = new Float32Array(analyser.fftSize);
      const tick = (): void => {
        if (!this.analyser) return;

        const now = performance.now();
        const dt = now - this.lastFrameTime;
        this.lastFrameTime = now;

        this.analyser.getFloatTimeDomainData(data);
        let sumSquares = 0;
        for (let i = 0; i < data.length; i++) {
          sumSquares += data[i] * data[i];
        }
        const rms = Math.sqrt(sumSquares / data.length);
        const dbfs = rms > 0 ? 20 * Math.log10(rms) : METER_MIN_DBFS;

        const normalized =
          (Math.max(METER_MIN_DBFS, Math.min(METER_MAX_DBFS, dbfs)) - METER_MIN_DBFS) /
          (METER_MAX_DBFS - METER_MIN_DBFS);
        const bars = Math.round(normalized * METER_BAR_COUNT);
        // Skip same-bar writes: the analyser runs at frame rate.
        if (bars !== this.litBars) {
          runInAction(() => {
            this.litBars = bars;
          });
        }

        if (dbfs >= THRESHOLD_DBFS) {
          this.aboveThresholdMs += dt;
          if (this.aboveThresholdMs >= THRESHOLD_HOLD_MS && this.phase !== "detected") {
            runInAction(() => {
              this.phase = "detected";
              this.micVerified = true;
            });
            this.clearSilenceTimer();
          }
        } else {
          this.aboveThresholdMs = 0;
        }

        this.rafId = requestAnimationFrame(tick);
      };
      this.rafId = requestAnimationFrame(tick);
    } catch {
      // Detection only; the silence timer still arms, so the escape hatch covers a failed meter.
    }
  }

  private stopMeter(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    this.sourceNode?.disconnect();
    this.sourceNode = null;
    this.analyser = null;
    if (this.audioContext) {
      void this.audioContext.close();
      this.audioContext = null;
    }
    runInAction(() => {
      this.litBars = 0;
    });
  }

  private teardownMicStream(): void {
    this.stopMeter();
    this.clearSilenceTimer();
    this.detachMicTrackListeners();
    this.micStream?.getTracks().forEach((track) => track.stop());
    this.micStream = null;
  }

  private async refreshDevices(): Promise<void> {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const audioInputs = devices.filter((d) => d.kind === "audioinput" && d.deviceId);
    const audioOutputs = devices.filter((d) => d.kind === "audiooutput" && d.deviceId);
    const videoInputs = devices.filter((d) => d.kind === "videoinput" && d.deviceId);
    runInAction(() => {
      this.audioDevices = audioInputs;
      this.speakerDevices = audioOutputs;
      this.cameraDevices = videoInputs;
    });
  }

  private handleGetUserMediaError(error: unknown): void {
    const name = getErrorName(error);
    runInAction(() => {
      if (name === "NotFoundError" || name === "OverconstrainedError") {
        this.phase = "no-device";
        // Retrying the same bad id would loop.
        this.selectedMicId = "default";
      } else if (name === "NotReadableError" || name === "TrackStartError") {
        this.phase = "busy";
      } else if (name === "NotSupportedError") {
        this.phase = "unsupported";
      } else {
        // Unclassified errors get the recoverable permission banner.
        this.phase = "denied";
      }
    });
    this.registerRecoveryListeners();
  }

  private handleDeviceChange = (): void => {
    void this.refreshDevices()
      .then(() => {
        if (this.phase === "no-device" && this.audioDevices.length > 0) {
          void this.start();
        }
      })
      .catch(() => {});
  };

  private registerRecoveryListeners(): void {
    if (typeof navigator === "undefined") return;

    if (!this.listenersRegistered) {
      navigator.mediaDevices.addEventListener(
        "devicechange",
        this.handleDeviceChange,
      );
      this.listenersRegistered = true;
    }

    if (this.phase === "denied") {
      void this.watchMicPermission();
    }
  }

  /** One live PermissionStatus handler at most. */
  private async watchMicPermission(): Promise<void> {
    if (!navigator.permissions?.query || this.micPermissionStatus) return;

    const gen = this.generation;
    try {
      const status = await navigator.permissions.query({
        name: "microphone" as PermissionName,
      });
      if (gen !== this.generation) return;
      this.micPermissionStatus = status;
      status.onchange = () => {
        if (status.state === "granted" && this.phase === "denied") {
          void this.start();
        }
      };
    } catch {
      // Safari doesn't support querying the "microphone" permission — the
      // user falls back to clicking "Try again".
    }
  }

  private clearPermissionWatch(): void {
    if (this.micPermissionStatus) {
      this.micPermissionStatus.onchange = null;
      this.micPermissionStatus = null;
    }
  }
}
